// When a live sample was taken, on this browser's clock (TOM-441). A frame's
// arrival is not its samples' age: a boat whose live lane is saturated sends a
// slow path in its minute batch, stamped about a minute before it arrives, and
// dated by arrival that reading looked live. The boat's own clock is not
// trusted outright either (motion.ts, anchorPrompt's clockSkewMs), so a boat's
// offset is measured: the smallest gap between a frame's arrival and its
// newest sample over the last minute. Fast-lane frames, a second or so behind,
// set it, so skew cancels and a batched sample keeps the lag it really has.

/** How long one frame's offset counts toward the boat's. */
export const CLOCK_WINDOW_MS = 60_000;

/** Consecutive frames that, all more than `CLOCK_STEP_MS` above the window's
 *  least offset, show the boat's clock has stepped back (TOM-447). */
export const CLOCK_STEP_RUN = 5;
export const CLOCK_STEP_MS = 60_000;

export interface ClockOffsetObservation {
  readonly at: number;
  readonly offsetMs: number;
}

/**
 * Records one frame's offset (its arrival minus its newest sample's time) and
 * returns the smallest in the window. Older observations drop out, so a boat
 * whose clock is corrected is followed within the window. A clock that steps
 * back is followed at once: when the last `CLOCK_STEP_RUN` frames all sit more
 * than `CLOCK_STEP_MS` above the least, the window starts again from them. A
 * stored window, a minute late, is one such frame among the fast lane's many,
 * never a run of them (TOM-447).
 */
export function observeClockOffset(
  window: ClockOffsetObservation[],
  at: number,
  offsetMs: number,
  windowMs: number = CLOCK_WINDOW_MS
): number {
  window.push({ at, offsetMs });
  while (window.length > 1 && at - window[0]!.at > windowMs) window.shift();
  let min = Infinity;
  for (const o of window) min = Math.min(min, o.offsetMs);
  const run = window.slice(-CLOCK_STEP_RUN);
  if (window.length > CLOCK_STEP_RUN && run.every((o) => o.offsetMs > min + CLOCK_STEP_MS)) {
    window.splice(0, window.length - CLOCK_STEP_RUN);
    min = Math.min(...run.map((o) => o.offsetMs));
  }
  return min;
}

/** A sample's time on this browser's clock: never later than its arrival. */
export function sampledAt(sampleMs: number, offsetMs: number, receivedAt: number): number {
  if (!Number.isFinite(sampleMs) || !Number.isFinite(offsetMs)) return receivedAt;
  return Math.min(receivedAt, sampleMs + offsetMs);
}

/** How far after its frame's arrival a held sample may be dated, on this
 *  clock, and still be trusted to rank by. The app uses the same minute. */
export const HELD_AHEAD_TOLERANCE_MS = 60_000;

/**
 * Whether an arriving sample replaces the one held for its path (TOM-447). By
 * the samples' own times: the newer wins, and a tie keeps the held one, as the
 * app folds. Every kind of frame arrives on one topic, and a stored telemetry
 * window, a minute old, can land after the live lane has given a fresher
 * sample of the same path; dated by arrival it set the reading back. A held
 * sample dated more than a minute after this frame's arrival is not trusted to
 * rank by, so a boat whose clock was wrong and then stepped back does not
 * freeze its readings until the clock catches up.
 */
export function replacesHeld(
  arrivingMs: number,
  heldMs: number,
  receivedAt: number,
  offsetMs: number
): boolean {
  if (!Number.isFinite(arrivingMs) || !Number.isFinite(heldMs)) return true;
  // Ahead as the boat stamped it (the app's rule) or on this clock through the
  // measured offset. Raw alone catches the step back itself: until the offset
  // window forgets it, the offset still carries the wrong clock, and corrected
  // time would call the held sample fine. A boat clock more than a minute fast
  // therefore folds by arrival, as in the app.
  const ahead = (ms: number) => ms > receivedAt + HELD_AHEAD_TOLERANCE_MS;
  if (ahead(heldMs) || ahead(heldMs + offsetMs)) return true;
  return arrivingMs > heldMs;
}

/**
 * Folds one frame's samples into the samples held per path, and returns the
 * ones that took their path's place, in frame order. A stored window's rows
 * run oldest first, so each path's newest row is taken last.
 */
export function foldFrame<S extends { path: string; time: string }>(
  held: Map<string, S>,
  samples: readonly S[],
  receivedAt: number,
  offsetMs: number
): S[] {
  const taken: S[] = [];
  for (const s of samples) {
    const prev = held.get(s.path);
    if (
      prev !== undefined &&
      !replacesHeld(Date.parse(s.time), Date.parse(prev.time), receivedAt, offsetMs)
    ) {
      continue;
    }
    held.set(s.path, s);
    taken.push(s);
  }
  return taken;
}
